import { sessionStorage as dibsSessionStorage } from 'dibs-browser-storage';
import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import { Mutable } from 'dibs-ts-utils/exports/Mutable';

import { userGeoInfoQueryResponse as GeoInfoQueryType } from './__generated__/userGeoInfoQuery.graphql';

type RegionalInfoType = ArrayElement<GeoInfoQueryType['viewer']['regionalInfo']>;
type ZipCodes = NonNullable<RegionalInfoType>['regionsByZipCode'];
type UserGeoInfo = RegionalInfoType & {
    timestamp: number;
};
export type RegionsByZipCode = {
    displayName: string;
    urlLabel: string;
};

export const USER_GEO_INFO = 'userGeoInfo';
// valid period is set to 30 days
const USER_ZIP_CODE_VALID_PERIOD = 2592000000;
// one day
const USER_ZIP_CODE_VALID_SHORT_PERIOD = 86400000;

// sessionUserZipCode is set when user enters to RefineMenu by hand
const setUserSessionZipCode = (userZipCode: string): void =>
    dibsSessionStorage.setItem('sessionUserZipCode', userZipCode);

const setUserSessionGeoInfo = (userGeoInfo?: RegionalInfoType): void => {
    const timestamp = Date.now();

    const { countryCode, zipCode, incomeLevel, regionsByZipCode } = userGeoInfo || {};
    const { incomeBracket, incomePercentile } = incomeLevel || {};

    dibsSessionStorage.setItem(USER_GEO_INFO, {
        timestamp,
        countryCode,
        zipCode,
        incomeLevel: {
            incomeBracket,
            incomePercentile,
        },
        regionsByZipCode: regionsByZipCode as Mutable<ZipCodes>,
    });
};

const getUserSessionGeoInfo = (): UserGeoInfo | null =>
    (dibsSessionStorage.getItem(USER_GEO_INFO) as UserGeoInfo) || null;

const hasValidUserSessionGeoInfo = (): boolean => {
    const geoInfo = getUserSessionGeoInfo();
    const { zipCode, regionsByZipCode, countryCode, incomeLevel, timestamp } = geoInfo || {};

    if (zipCode && countryCode && regionsByZipCode && timestamp) {
        const timeDiff = Date.now() - timestamp;

        // "0" is valid zipCode set from digital elements for private IP addresses
        const validPeriod =
            zipCode === '0' ? USER_ZIP_CODE_VALID_SHORT_PERIOD : USER_ZIP_CODE_VALID_PERIOD;

        // Checking for users which has valid zipCode
        // But doesn't have incomeLevel
        // Will fetch incomeLevel with zipCode stored in localStorage as arg
        if (typeof incomeLevel === 'undefined' && zipCode !== '0') {
            return false;
        }

        if (!timeDiff || timeDiff <= validPeriod) {
            return true;
        }
    }
    return false;
};

const getUserSessionCountryCode = (): string => {
    const userGeoInfo = getUserSessionGeoInfo();
    return userGeoInfo?.countryCode || '';
};

const hasUserSessionCountryCode = (): boolean => !!getUserSessionCountryCode();

const getUserZipCode = (): string => {
    const userGeoInfo = getUserSessionGeoInfo();
    const userSessionZipCode = dibsSessionStorage.getItem('sessionUserZipCode') || '';
    return (userSessionZipCode || userGeoInfo?.zipCode || '') as string;
};

const getUserSessionRegions = (): RegionsByZipCode[] | undefined => {
    const userGeoInfo = getUserSessionGeoInfo();
    return userGeoInfo?.regionsByZipCode?.map<RegionsByZipCode>(region => ({
        displayName: region?.displayName || '',
        urlLabel: region?.urlLabel || '',
    }));
};

const incomeLevelMap: Record<string, string> = {
    higher: 'tier 1',
    high: 'tier 2',
    mid: 'tier 3',
    low: 'tier 4',
};

const getIncomeLevelInfo = (regionalInfo: RegionalInfoType): string | undefined => {
    const geoInfo = getUserSessionGeoInfo();
    const incomeLevel = geoInfo?.incomeLevel || regionalInfo?.incomeLevel;
    const { incomeBracket, incomePercentile } = incomeLevel || {};
    return incomeBracket && incomePercentile
        ? `${incomeLevelMap[incomeBracket.toLowerCase()]}| ${incomePercentile}`
        : undefined;
};

export {
    getUserZipCode,
    getIncomeLevelInfo,
    getUserSessionRegions,
    setUserSessionGeoInfo,
    getUserSessionGeoInfo,
    hasValidUserSessionGeoInfo,
    getUserSessionCountryCode,
    hasUserSessionCountryCode,
    setUserSessionZipCode,
};
